import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type IntegrationLogItem } from '@/api/routes/partners/partners.integration.api';
import { formatIntegrationCount, formatIntegrationDate } from '@/domain/integration/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  log: IntegrationLogItem[];
}

/** P17 "Atividade": hourly API calls of the last 30 days and the key history. Counts only. */
const IntegrationActivityLog = ({ log }: Props) => {
  const t = useTranslate('domain.integration.components.IntegrationActivityLog');
  const count = (value: number) => ({ count: value, value: formatIntegrationCount(value) });

  const describe = (line: IntegrationLogItem): { title: string; detail?: string } => {
    switch (line.kind) {
      case 'feed_read': {
        const parts = [
          line.counts.subjects ? t('feed.subjects', count(line.counts.subjects)) : null,
          line.counts.revoked ? t('feed.revoked', count(line.counts.revoked)) : null,
        ].filter((part): part is string => Boolean(part));
        return { title: t('feed.title'), detail: parts.join(', ') };
      }
      case 'media_received':
        return {
          title: t('received.title', count(line.counts.media)),
          detail: t('received.requests', count(line.counts.requests)),
        };
      case 'media_rejected':
        return {
          title: t('rejected.title', count(line.counts.media)),
          detail: t('rejected.reason'),
        };
      case 'key_created':
        return { title: t('keyCreated') };
      case 'key_revoked':
        return { title: t('keyRevoked') };
    }
  };

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('title')}
      </Typography>
      {log.length ? (
        <Stack component="ul" divider={<Divider component="li" />} gap={1} sx={sx.list}>
          {log.map((line) => {
            const { title, detail } = describe(line);
            return (
              <Stack component="li" key={`${line.kind}-${line.at}`} gap={0.25}>
                <Typography variant="body2">
                  {title}
                  {detail ? (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {` · ${detail}`}
                    </Typography>
                  ) : null}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatIntegrationDate(line.at)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('empty')}
        </Typography>
      )}
    </Paper>
  );
};

export default IntegrationActivityLog;

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  list: { listStyle: 'none', m: 0, p: 0 },
};
